import React, {useState, useEffect} from 'react';

import './Teams.scss';
import {useLazyQuery} from "@apollo/client";
import gqlSchemaLibrary from "../services/graphql.schemas";
import TeamBase from "./Teams.team";
import {Subject} from "rxjs";
import {Link} from "react-router-dom";
import ButtonIcon from "../forms/buttons/Button.icon";
import {clearGqlTypename} from "../utils/clearTypename";
import TeamMembersDialog from "./dialogs/Team.members.dialog";
import TeamDeleteDialog from "./dialogs/Team.delete.dialog";
import TeamProgramsDialog from "./dialogs/Team.programs.dialog";
import Checkbox from "../forms/inputs/Checkboxk";
import ItemMenu from "../forms/menu/MenuComponent";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../services/interactions/page.view";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";

function Teams({ currentOrg, toaster, currentUser }) {
    const {t} = useTranslation(['common','teams']);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('name');

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());

    const [teams, setTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [hoverTeam, setHoverTeam] = useState(null);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [openMembersModal, setOpenMembersModal] = useState(false);
    const [openTeamDeleteModal, setOpenTeamDeleteModal] = useState(false);
    const [openTeamProgramsModal, setOpenTeamProgramsModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    const [getTeams] = useLazyQuery(gqlSchemaLibrary.teams.queries.getTeams, {
        onCompleted: ({getTeams}) => {
            setTeams(clearGqlTypename(getTeams));
        },
        fetchPolicy: 'cache-and-network'
    })

    useEffect(() => {
        if (!currentUser.userRoles.userPatients.edit) {
            window.history.back();
        }
        getTeams({});
    }, [getTeams]);

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.TEAMS_LIST, {tag: 'input', event: 'onSearch', action: 'input search teams', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const sortList = (key) => {
        return () => {
            AuditTrail.sendLog(PAGES.TEAMS_LIST, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setSorting(key);
            setSortAscending(!sortAscending);
        }
    }

    const filteredTeams = teams.filter(t => {
        if (!search) {
            return true;
        }

        const name = (t.name || '').toLowerCase();
        const searchWords = search.split(' ').map(w => w.toLowerCase());
        return searchWords.reduce((p, word) => {
            return p && name.includes(word);
        }, true);
    }).sort((a, b) => {
        if(!sorting){
            return true;
        }
        if(sorting === 'name'){
            if(sortAscending){
                return a.name.localeCompare(b.name);
            }else{
                return b.name.localeCompare(a.name);
            }
        }
        return false;
    });

    const onOpenMembersModal = (team) => {
        setSelectedTeam(team);
        setOpenMembersModal(true);
    }

    const onCloseModal = (team, data) => {
        if (team && data) {
            const _teams = Object.assign([], teams);
            const _team = _teams.find((t) => t._id === team._id);
            const index = _teams.indexOf(_team);
            if (index > -1){
                _team.users = data.users;
                _team.teams = data.teams;
                setTeams(_teams);
            }
        }
        setOpenMembersModal(false);
        setSelectedTeam(null);
    }

    const onOpenDeleteModal = (team) => {
        setSelectedTeam(team);
        setOpenTeamDeleteModal(true);
    }

    const closeDeleteModal = (_teams) => {
        if (_teams) {
            for (const _t of _teams) {
                const _team = teams.find((t) => t._id === _t._id);
                const index = teams.indexOf(_team);
                if (index > -1) {
                    teams.splice(index, 1);
                }
            }
            setTeams(teams);
        }
        setSelectedTeam(null);
        setOpenTeamDeleteModal(false);
    }

    const onChange = (team) => {
        const _team = selectedTeams.find((t) => t._id === team._id);
        const index = selectedTeams.indexOf(_team);
        const checked = index > -1;
        if (checked) {
            selectedTeams.splice(index, 1);
        } else {
            selectedTeams.push(team);
        }
        AuditTrail.sendLog(PAGES.TEAMS_LIST, {tag: 'checkbox', event: 'onCheck', action: 'button selected team', team_id: team._id, check: !checked});
        if (!selectedTeams.length) {
            setSelectAll(false);
        } else if (selectedTeams.length && selectedTeams.length === filteredTeams.length) {
            setSelectAll(true);
        }
        setSelectedTeams(Object.assign([], selectedTeams));
    }

    const onSelectAll = () => {
        const status = !selectAll;
        if (status) {
            setSelectedTeams(Object.assign([], teams));
        } else {
            setSelectedTeams([]);
        }
        setSelectAll(status);
        AuditTrail.sendLog(PAGES.TEAMS_LIST, {tag: 'checkbox', event: 'onCheck', action: 'button selected all teams', check: status});
    }

    const onAddProgramsTeam = () => {
        if (selectedTeams.length) {
            setOpenTeamProgramsModal(true);
        }
    }

    const onDeleteTeams = () => {
        if (selectedTeams.length) {
            setOpenTeamDeleteModal(true);
        }
    }

    const onOpenProgramsModal = (team) => {
        setSelectedTeam(team);
        setOpenTeamProgramsModal(true);
    }

    const closeProgramsModal = (_teams, programs) => {
        if (_teams) {
            getTeams({});
        }
        setSelectedTeam(null);
        setOpenTeamProgramsModal(false);
    }

    return (
        <div id="TeamsBase">
            <div className="filter">
                <p>
                </p>
                <div className={`search ${searchFocused ? 'focused' : ''}`}>
                    <input value={search} placeholder={t('search')} onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                    <i className="material-icons">
                        search
                    </i>
                </div>
                <div className="addAction">
                    <p className="_counter">
                        {filteredTeams.length} team{filteredTeams.length === 1? '' : 's'}
                    </p>
                    {
                        !currentUser.userRoles.userClinicians.create ?
                            ''
                            :
                            <Link to='/teams/create'>
                                <div className="enrollAction">
                                    <ButtonIcon>
                                        <span className="material-icons">
                                            add
                                        </span>
                                    </ButtonIcon>
                                    <p>{t('new')}</p>
                                </div>
                            </Link>
                    }
                </div>
            </div>
            <div className="teamsList scrollBar">
                <div className="headers">
                    <div>
                        <Checkbox bool={selectAll} onChange={onSelectAll}/>
                    </div>
                    <div className="selectable sortable">
                        <p onClick={sortList('name')}>
                            {t('name')}
                        </p>
                        <div className="_updown" onClick={sortList('name')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('teams:members')}
                        </p>
                    </div>
                    <div className="selectable">
                        <p>
                            {t('teams:programs')}
                        </p>
                    </div>

                    <div>
                        {
                            selectedTeams.length?
                                <ItemMenu actionWillRedirect={false} fixed={true}>
                                    <>
                                        <div className="_option" onClick={onAddProgramsTeam} >
                                            <p>{t('teams:add-to-program')}</p>
                                        </div>
                                        <div className="_option" onClick={onDeleteTeams}>
                                            <p>{t('teams:remove-teams')}</p>
                                        </div>
                                    </>
                                </ItemMenu> : ''
                        }

                    </div>
                </div>
                {
                    filteredTeams.map((team, index) => (
                        <TeamBase key={index} team={team} selectedTeams={selectedTeams} hoverTeam={hoverTeam} onHover={setHoverTeam}
                                  onAddMembers={onOpenMembersModal} onDeleteTeam={onOpenDeleteModal} onProgramTeam={onOpenProgramsModal} onChange={onChange} />
                    ))
                }
            </div>
            {
                selectedTeam?
                    <TeamMembersDialog event={PAGES.TEAMS_LIST} team={selectedTeam}
                                       openModal={openMembersModal} closeModal={onCloseModal} /> : ''
            }
            {
               <TeamDeleteDialog event={PAGES.TEAMS_LIST} team={selectedTeam} teams={selectedTeams} redirect={false} openModal={openTeamDeleteModal} closeModal={closeDeleteModal} />
            }
            {
                <TeamProgramsDialog event={PAGES.TEAMS_LIST} team={selectedTeam} teams={selectedTeams} openModal={openTeamProgramsModal} closeModal={closeProgramsModal} />
            }

        </div>
    );
}

export default PageViewLog(Teams);
